import ClassListStore from 'components/ClassesV2/List/store';
import {
  isDashboardDefaultModalities,
  isDashboardDefaultStatuses,
  isEndDateFilterAtDefault,
  LMS_CLASS_DEFAULT_SORT,
  LMS_CLASS_DEFAULT_SORT_BY,
} from 'components/ClassesV2/List/classList.filterDefaults';
import type { WeekFilterKey } from 'components/ClassesV2/List/classList.types';
import PremiumSessionStore from 'components/ClassesV2/PremiumSession/store';
import { isPremiumDashboardDefaultStatuses } from 'components/ClassesV2/PremiumSession/premiumSession.filterDefaults';
import {
  buildRoutePath,
  createCommaSeparatedCodec,
  createEnumMapCodec,
  decodeValueFromUrl,
  defineRouteFilters,
  encodeValueForUrl,
} from 'components/RouteFilters';
import type { ActiveFilters } from 'components/RouteFilters';
import { PREMIUM_INSTRUCTOR_DASHBOARD_SORT_FILEDS } from 'config/constants';

export const INSTRUCTOR_DASHBOARD_FILTER_KEYS = [
  'tab',
  'q',
  'instructors',
  'modalities',
  'status',
  'week',
  'start',
  'end',
  'sort',
  'order',
] as const;

export const INSTRUCTOR_DASHBOARD_ROUTE_NAME =
  'instructor-dashboard:class-list';

export const CREATE_CLASS_FROM_INSTRUCTOR_DASHBOARD = 'instructor-dashboard';

export const getCreateClassReturnRoute = (
  from?: string | null,
): typeof INSTRUCTOR_DASHBOARD_ROUTE_NAME | 'classes:list' =>
  from === CREATE_CLASS_FROM_INSTRUCTOR_DASHBOARD
    ? INSTRUCTOR_DASHBOARD_ROUTE_NAME
    : 'classes:list';

const INSTRUCTOR_DASHBOARD_BASE_PATH = '/instructor-dashboard';

export const INSTRUCTOR_DASHBOARD_DEFAULT_TAB = 'virtual-training' as const;

export type InstructorDashboardTab =
  | typeof INSTRUCTOR_DASHBOARD_DEFAULT_TAB
  | 'premium-sessions';

type InstructorDashboardFilterKey =
  (typeof INSTRUCTOR_DASHBOARD_FILTER_KEYS)[number];

type InstructorDashboardActiveFilters =
  ActiveFilters<InstructorDashboardFilterKey>;

const instructorDashboardRouteFilters = defineRouteFilters(
  INSTRUCTOR_DASHBOARD_FILTER_KEYS,
);

export const isInstructorDashboardFilterKey =
  instructorDashboardRouteFilters.isKnownFilterKey;

export const INSTRUCTOR_DASHBOARD_ROUTE_PATH = buildRoutePath(
  INSTRUCTOR_DASHBOARD_BASE_PATH,
  INSTRUCTOR_DASHBOARD_FILTER_KEYS,
);

const WEEK_TOKEN_FROM_URL: Record<string, WeekFilterKey> = {
  this: 'classesThisWeek',
  next: 'classesNextWeek',
};

const WEEK_TOKEN_TO_URL: Record<WeekFilterKey, string> = {
  classesThisWeek: 'this',
  classesNextWeek: 'next',
};

const instructorDashboardFilterCodecs = {
  instructors: createCommaSeparatedCodec(),
  modalities: createCommaSeparatedCodec(),
  status: createCommaSeparatedCodec(),
  week: createEnumMapCodec<WeekFilterKey>({
    fromUrl: WEEK_TOKEN_FROM_URL,
    toUrl: WEEK_TOKEN_TO_URL,
  }),
};

type InstructorDashboardParsedFilters = {
  tab: InstructorDashboardTab;
  search: string;
  instructors: string[];
  modalities: string[];
  statuses: string[];
  weekFilterKeys: WeekFilterKey[];
  startDateTime: string | null;
  endDateTime: string | null;
  sortBy: string;
  sort: string;
};

type InstructorDashboardFilterStore = {
  instructors: string[];
  modalities: string[];
  statuses: string[];
  weekFilterKeys: WeekFilterKey[];
  startDateTime: string | null;
  endDateTime: string | null;
  search: string;
  sort: string;
  sortBy: string;
  sortByOverride?: string;
  defaultInstructorSelectionApplied: boolean;
  onFilterChange: (pageOffset?: number, signal?: AbortSignal) => void;
  setPremiumSort?: (sortBy: string, sort: string) => void;
};

type InstructorDashboardSyncContext = {
  activeTab: InstructorDashboardTab;
  virtualTrainingStore: InstanceType<typeof ClassListStore>;
  premiumSessionsStore: InstanceType<typeof PremiumSessionStore>;
};

type InstructorDashboardBuildUrlOptions = {
  includeInstructorsInUrl: boolean;
};

type InstructorDashboardFetchPolicy = 'immediate' | 'debounced' | 'none';

const readTabFromUrl = (tabFromUrl?: string): InstructorDashboardTab => {
  const isPremiumSessionsTab = tabFromUrl === 'premium-sessions';
  return isPremiumSessionsTab
    ? 'premium-sessions'
    : INSTRUCTOR_DASHBOARD_DEFAULT_TAB;
};

export const isInstructorDashboardBrowserReload = (): boolean => {
  if (
    typeof performance === 'undefined' ||
    typeof performance.getEntriesByType !== 'function'
  ) {
    return false;
  }

  const [navigation] = performance.getEntriesByType(
    'navigation',
  ) as PerformanceNavigationTiming[];

  return navigation?.type === 'reload';
};

export const hasInstructorDashboardNonTabRouteParams = (
  routeParams: Record<string, string> = {},
): boolean =>
  INSTRUCTOR_DASHBOARD_FILTER_KEYS.some(
    (key) => key !== 'tab' && Boolean(routeParams[key]),
  );

export function parseInstructorDashboardRouteParams(
  routeParams: Record<string, string> = {},
): InstructorDashboardParsedFilters {
  const activeFilters =
    instructorDashboardRouteFilters.readFromRoute(routeParams);

  return {
    tab: readTabFromUrl(activeFilters.tab),
    search: activeFilters.q?.trim() || '',
    instructors: (decodeValueFromUrl(
      'instructors',
      activeFilters.instructors || '',
      instructorDashboardFilterCodecs,
    ) || []) as string[],
    modalities: (decodeValueFromUrl(
      'modalities',
      activeFilters.modalities || '',
      instructorDashboardFilterCodecs,
    ) || []) as string[],
    statuses: (decodeValueFromUrl(
      'status',
      activeFilters.status || '',
      instructorDashboardFilterCodecs,
    ) || []) as string[],
    weekFilterKeys: (decodeValueFromUrl(
      'week',
      activeFilters.week || '',
      instructorDashboardFilterCodecs,
    ) || []) as WeekFilterKey[],
    startDateTime: activeFilters.start || null,
    endDateTime: activeFilters.end || null,
    sortBy: activeFilters.sort || LMS_CLASS_DEFAULT_SORT_BY,
    sort: activeFilters.order || LMS_CLASS_DEFAULT_SORT,
  };
}

const readPremiumSortByForUrl = (
  store: InstructorDashboardFilterStore,
): string => {
  const isSortedBySessionTitle =
    store.sortByOverride ===
    PREMIUM_INSTRUCTOR_DASHBOARD_SORT_FILEDS.sessionTitle;

  return isSortedBySessionTitle ? 'name' : 'startDate';
};

const isDefaultSortSelection = (sortBy: string, sort: string): boolean =>
  sortBy === LMS_CLASS_DEFAULT_SORT_BY && sort === LMS_CLASS_DEFAULT_SORT;

export const hasInstructorDashboardFiltersInUrl = (
  parsedFilters: InstructorDashboardParsedFilters,
): boolean => {
  const isPremiumTab = parsedFilters.tab === 'premium-sessions';
  const statusesAreNonDefault = isPremiumTab
    ? !isPremiumDashboardDefaultStatuses(parsedFilters.statuses) &&
      parsedFilters.statuses.length > 0
    : !isDashboardDefaultStatuses(parsedFilters.statuses) &&
      parsedFilters.statuses.length > 0;

  return Boolean(
    parsedFilters.search ||
      parsedFilters.instructors.length > 0 ||
      (!isDashboardDefaultModalities(parsedFilters.modalities) &&
        parsedFilters.modalities.length > 0) ||
      statusesAreNonDefault ||
      parsedFilters.weekFilterKeys.length > 0 ||
      parsedFilters.startDateTime ||
      (parsedFilters.endDateTime &&
        !isEndDateFilterAtDefault(parsedFilters.endDateTime)) ||
      !isDefaultSortSelection(parsedFilters.sortBy, parsedFilters.sort),
  );
};

const hasRouteParam = (
  routeParams: Record<string, string>,
  key: InstructorDashboardFilterKey,
): boolean => key in routeParams;

function buildInstructorDashboardRouteParams(
  store: InstructorDashboardFilterStore,
  context: InstructorDashboardSyncContext,
  options: InstructorDashboardBuildUrlOptions,
): InstructorDashboardActiveFilters {
  const isPremiumTab = context.activeTab === 'premium-sessions';
  const includeInstructors = options.includeInstructorsInUrl;
  const sortBy = isPremiumTab ? readPremiumSortByForUrl(store) : store.sortBy;
  const sort = store.sort || LMS_CLASS_DEFAULT_SORT;
  const isDefaultSort = isDefaultSortSelection(sortBy, sort);

  const activeFilters: InstructorDashboardActiveFilters = {
    tab: context.activeTab,
    q: store.search.trim() || undefined,
    week: encodeValueForUrl(
      'week',
      store.weekFilterKeys,
      instructorDashboardFilterCodecs,
    ),
    start: store.startDateTime || undefined,
  };

  const isDefaultStatusSelection = isPremiumTab
    ? isPremiumDashboardDefaultStatuses(store.statuses)
    : isDashboardDefaultStatuses(store.statuses);

  if (!isDefaultStatusSelection) {
    activeFilters.status = encodeValueForUrl(
      'status',
      store.statuses,
      instructorDashboardFilterCodecs,
    );
  }

  if (store.endDateTime && !isEndDateFilterAtDefault(store.endDateTime)) {
    activeFilters.end = store.endDateTime;
  }

  if (includeInstructors) {
    activeFilters.instructors = encodeValueForUrl(
      'instructors',
      store.instructors,
      instructorDashboardFilterCodecs,
    );
  }

  const hasSelectedModalities =
    !isPremiumTab &&
    store.modalities.length > 0 &&
    !isDashboardDefaultModalities(store.modalities);
  if (hasSelectedModalities) {
    activeFilters.modalities = encodeValueForUrl(
      'modalities',
      store.modalities,
      instructorDashboardFilterCodecs,
    );
  }

  if (!isDefaultSort) {
    activeFilters.sort = sortBy;
    activeFilters.order = sort;
  }

  return activeFilters;
}

export function buildInstructorDashboardNavigateParams(
  store: InstructorDashboardFilterStore,
  context: InstructorDashboardSyncContext,
  options: InstructorDashboardBuildUrlOptions,
): Record<string, string | undefined> {
  return instructorDashboardRouteFilters.writeToRoute(
    buildInstructorDashboardRouteParams(store, context, options),
  );
}

const resolveInstructorDashboardStore = (
  context: InstructorDashboardSyncContext,
): InstructorDashboardFilterStore => {
  const isPremiumTab = context.activeTab === 'premium-sessions';
  return isPremiumTab
    ? context.premiumSessionsStore
    : context.virtualTrainingStore;
};

export function buildInstructorDashboardTabRouteParams(
  tab: InstructorDashboardTab,
  context: InstructorDashboardSyncContext,
  options: InstructorDashboardBuildUrlOptions,
): Record<string, string | undefined> {
  const tabContext = {
    ...context,
    activeTab: tab,
  };
  const targetStore = resolveInstructorDashboardStore(tabContext);
  const activeFilters = buildInstructorDashboardRouteParams(
    targetStore,
    tabContext,
    options,
  );

  return instructorDashboardRouteFilters.writeToRoute(activeFilters);
}

/* eslint-disable no-param-reassign -- MobX store observables are mutated in place */
export function applyInstructorDashboardFiltersToStore(
  store: InstructorDashboardFilterStore,
  parsedFilters: InstructorDashboardParsedFilters,
  context: InstructorDashboardSyncContext,
  fetchPolicy: InstructorDashboardFetchPolicy = 'none',
  routeParams: Record<string, string> = {},
): void {
  const isPremiumTab = context.activeTab === 'premium-sessions';

  store.search = parsedFilters.search;

  if (!isPremiumTab && hasRouteParam(routeParams, 'modalities')) {
    store.modalities = [...parsedFilters.modalities];
  }

  if (hasRouteParam(routeParams, 'status')) {
    store.statuses = [...parsedFilters.statuses];
  }

  if (hasRouteParam(routeParams, 'week')) {
    store.weekFilterKeys = [...parsedFilters.weekFilterKeys];
  } else {
    store.weekFilterKeys = [];
  }

  if (!isPremiumTab) {
    context.virtualTrainingStore.syncWeekFilterDateRange?.();
  } else {
    (
      store as InstructorDashboardFilterStore & {
        onSeeClassesChange?: () => void;
      }
    ).onSeeClassesChange?.();
  }

  if (hasRouteParam(routeParams, 'start')) {
    store.startDateTime = parsedFilters.startDateTime;
  } else if (!hasRouteParam(routeParams, 'week')) {
    store.startDateTime = null;
  }

  if (hasRouteParam(routeParams, 'end')) {
    store.endDateTime = parsedFilters.endDateTime;
  } else {
    (
      store as InstructorDashboardFilterStore & {
        ensureDefaultEndDateFilter?: () => void;
      }
    ).ensureDefaultEndDateFilter?.();
  }

  const hasInstructorsFromUrl = parsedFilters.instructors.length > 0;
  if (hasInstructorsFromUrl) {
    store.instructors = [...parsedFilters.instructors];
    store.defaultInstructorSelectionApplied = true;
  }

  if (isPremiumTab && store.setPremiumSort) {
    store.setPremiumSort(parsedFilters.sortBy, parsedFilters.sort);
    if (fetchPolicy === 'immediate') {
      store.onFilterChange(0);
    }
    return;
  }

  store.sortBy = parsedFilters.sortBy;
  store.sort = parsedFilters.sort;

  if (fetchPolicy === 'immediate') {
    store.onFilterChange(isPremiumTab ? 0 : undefined);
  }
}
/* eslint-enable no-param-reassign */
